import type { MorningLights, Takings } from '@goodtime/shared';

/**
 * The words on the takings card (on leaving a road slot) and the morning
 * card (the day's first visit). The cards decide nothing: they read what
 * the server already counted or paid.
 */

/** "Tonight's" from 5 pm to 4 am, "Today's" otherwise. */
export function takingsTitle(hour: number): string {
  return hour >= 17 || hour < 4 ? "TONIGHT'S TAKINGS" : "TODAY'S TAKINGS";
}

export interface TakingsRow {
  label: string;
  value: string;
  /** Which painted icon sits beside it. */
  icon: 'coin' | 'run' | 'star' | 'spin' | 'gem';
}

const n = (v: number) => v.toLocaleString('en-GB');

/** The rows worth showing: coins won always; the rest only when there's
 *  something to say. */
export function takingsRows(t: Takings, runName: string): TakingsRow[] {
  const rows: TakingsRow[] = [{ label: 'Coins won', value: n(t.coinsWon), icon: 'coin' }];
  if (t.biggestWin > 0) rows.push({ label: 'Biggest win', value: n(t.biggestWin), icon: 'star' });
  if (t.lampRuns > 0) rows.push({ label: `${runName}s`, value: n(t.lampRuns), icon: 'run' });
  if (t.freeSpinsWon > 0) rows.push({ label: 'Free spins won', value: n(t.freeSpinsWon), icon: 'spin' });
  if (t.gemsWon > 0) rows.push({ label: 'Gems found', value: n(t.gemsWon), icon: 'gem' });
  return rows;
}

/** How the session went, said plainly (up, level or down), then kindly.
 *  Never a nudge to win anything back. */
export function takingsLine(t: Takings, lights: { ready: number; readyMax: number } | null, storyOn: boolean): string {
  const keep = lights && lights.ready > 0
    ? ` Your ${lights.ready} side light${lights.ready === 1 ? '' : 's'} will keep till next time.`
    : '';
  const net = t.coinsWon - t.coinsSpent;
  if (net > 0) return `${storyOn ? 'Good playing!' : 'Nicely done.'} You finished ${n(net)} coins up.${keep}`;
  if (net === 0) return `You finished level.${keep}`;
  return `${n(-net)} coins down this time.${keep || ' Every spin still lit your side lights.'}`;
}

export interface MorningCopy {
  title: string;
  /** What the host says (Fen, or the house in Classic mode). */
  line: string;
  /** "Day 3 in a row". */
  streak: string;
}

/** The morning card's words. `slotName` is the slot whose side lights the
 *  claim lit (`claim.readyLights.themeId`, the road played last), which may
 *  not be the one on screen. */
export function morningCopy(
  claim: { reward: number; streak: number; readyLights?: MorningLights | null },
  slotName: string,
  storyOn: boolean,
): MorningCopy {
  const coins = `${n(claim.reward)} coins`;
  const lit = claim.readyLights && claim.readyLights.added > 0 ? claim.readyLights.added : 0;
  const lights = lit === 0 ? '' : ` and ${lit} side light${lit === 1 ? '' : 's'} already lit on ${slotName}`;
  const line = storyOn
    ? lit > 0
      ? `Morning! Here are ${coins} for today, and I've lit ${lit} of your side lights on ${slotName}.`
      : `Morning! Here are ${coins} for today.`
    : `Here are ${coins} for today${lights}.`;
  return {
    title: storyOn ? 'MORNING POST' : 'GOOD MORNING',
    line,
    streak: claim.streak <= 1 ? 'Come back tomorrow for more.' : `Day ${claim.streak} in a row.`,
  };
}
